/**
 * The views of the accounts screens, as the address bar holds them. A
 * default is not written into the address.
 *
 * What a list is searched for (a name, a phone) is not part of a view: that
 * is personal data, kept out of the address (`@/shared/lib/private-search`).
 */
import type { AccountsFilter } from "../api/queries";
import {
  ACCOUNT_STATUSES,
  SORTS,
  STAFF_ROLES,
  type AccountStatus,
  type Sort,
  type StaffRole,
} from "../api/types";

function pageOf(value: unknown): number | undefined {
  const page = Number(value);
  return Number.isInteger(page) && page > 1 ? page : undefined;
}

// ---- one client ----

/** What a client's orders add up to: the statuses their list can be narrowed to. */
export const ORDER_STATUSES = ["open", "completed", "cancelled"] as const;
export type ClientOrderStatus = (typeof ORDER_STATUSES)[number];

/**
 * The view of a client's page, as the address bar holds it: which of their
 * orders are listed, made between which days, and which page of them.
 */
export interface ClientSearch {
  /** Only their orders that stand so. */
  orders?: ClientOrderStatus;
  /** Made on this day or after it, as `YYYY-MM-DD` by the market's clock. */
  from?: string;
  /** Made on this day or before it. */
  to?: string;
  /** The page of their orders. */
  opage?: number;
}

function dayOf(value: unknown): string | undefined {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  return Number.isNaN(Date.parse(`${value}T00:00:00Z`)) ? undefined : value;
}

/** Reads the address. Anything it does not know is dropped, never trusted. */
export function parseClientSearch(raw: Readonly<Record<string, unknown>>): ClientSearch {
  const search: ClientSearch = {};
  const status = ORDER_STATUSES.find((known) => known === raw["orders"]);
  if (status !== undefined) search.orders = status;
  const from = dayOf(raw["from"]);
  if (from !== undefined) search.from = from;
  const to = dayOf(raw["to"]);
  if (to !== undefined) search.to = to;
  const page = pageOf(raw["opage"]);
  if (page !== undefined) search.opage = page;
  return search;
}

/** The view after the orders' filters changed: their first page. */
export function withOrderFilters(filters: {
  orders?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
}): ClientSearch {
  const next: ClientSearch = {};
  const status = ORDER_STATUSES.find((known) => known === filters.orders);
  if (status !== undefined) next.orders = status;
  const from = dayOf(filters.from);
  if (from !== undefined) next.from = from;
  const to = dayOf(filters.to);
  if (to !== undefined) next.to = to;
  return next;
}

export function withOrdersPage(search: ClientSearch, page: number): ClientSearch {
  const { opage: _, ...rest } = search;
  return page > 1 ? { ...rest, opage: page } : rest;
}

// ---- clients ----

export const DEFAULT_SORT: Sort = "-created_at";

export interface ClientsSearch {
  page?: number;
  status?: AccountStatus;
  sort?: Sort;
}

/** Reads the address. Anything it does not know is dropped, never trusted. */
export function parseClientsSearch(raw: Readonly<Record<string, unknown>>): ClientsSearch {
  const search: ClientsSearch = {};
  const page = pageOf(raw["page"]);
  if (page !== undefined) search.page = page;
  const status = ACCOUNT_STATUSES.find((known) => known === raw["status"]);
  if (status !== undefined) search.status = status;
  const sort = SORTS.find((known) => known === raw["sort"]);
  if (sort !== undefined && sort !== DEFAULT_SORT) search.sort = sort;
  return search;
}

/**
 * What the clients list asks `GET /v2/admin/users` for: the accounts holding
 * the client's role. `q` is what the list is searched for (part of the name,
 * the phone or the display id): it comes from the screen, never from the address.
 */
export function clientsFilter(search: ClientsSearch, q?: string): AccountsFilter {
  const filter: AccountsFilter = { role: "client", sort: search.sort ?? DEFAULT_SORT };
  if (search.status !== undefined) filter.status = search.status;
  if (q !== undefined) filter.q = q;
  return filter;
}

/** The view after a filter changed: its first page. */
export function withClientFilters(filters: {
  status?: string | undefined;
  sort?: string | undefined;
}): ClientsSearch {
  const next: ClientsSearch = {};
  const status = ACCOUNT_STATUSES.find((known) => known === filters.status);
  if (status !== undefined) next.status = status;
  const sort = SORTS.find((known) => known === filters.sort);
  if (sort !== undefined && sort !== DEFAULT_SORT) next.sort = sort;
  return next;
}

// ---- phone blocks ----

export interface BlocksSearch {
  page?: number;
  /** The lifted blocks too. */
  all?: boolean;
}

export function parseBlocksSearch(raw: Readonly<Record<string, unknown>>): BlocksSearch {
  const search: BlocksSearch = {};
  const page = pageOf(raw["page"]);
  if (page !== undefined) search.page = page;
  if (raw["all"] === true || raw["all"] === "true") search.all = true;
  return search;
}

// ---- staff ----

/** The tabs of the staff list: one a role, the many first. */
export const STAFF_TABS = [
  "dispatcher",
  "admin",
  "superadmin",
] as const satisfies readonly StaffRole[];
export const DEFAULT_STAFF_TAB: StaffRole = "dispatcher";

export interface StaffSearch {
  role?: StaffRole;
  /** Only the accounts that stand so: the blocked ones, say. */
  status?: AccountStatus;
  page?: number;
}

export function parseStaffSearch(raw: Readonly<Record<string, unknown>>): StaffSearch {
  const search: StaffSearch = {};
  const role = STAFF_ROLES.find((known) => known === raw["role"]);
  if (role !== undefined && role !== DEFAULT_STAFF_TAB) search.role = role;
  const status = ACCOUNT_STATUSES.find((known) => known === raw["status"]);
  if (status !== undefined) search.status = status;
  const page = pageOf(raw["page"]);
  if (page !== undefined) search.page = page;
  return search;
}

/**
 * What the staff list asks `GET /v2/admin/users` for: the accounts holding
 * the tab's role, by name, narrowed to how the account stands when asked.
 */
export function staffFilter(search: StaffSearch, q?: string): AccountsFilter {
  const filter: AccountsFilter = { role: staffTabOf(search), sort: "full_name" };
  if (search.status !== undefined) filter.status = search.status;
  if (q !== undefined) filter.q = q;
  return filter;
}

export function staffTabOf(search: StaffSearch): StaffRole {
  return search.role ?? DEFAULT_STAFF_TAB;
}

/** The view of another role: its first page, how the accounts stand kept. */
export function withStaffTab(role: StaffRole, search: StaffSearch = {}): StaffSearch {
  const next: StaffSearch = {};
  if (role !== DEFAULT_STAFF_TAB) next.role = role;
  if (search.status !== undefined) next.status = search.status;
  return next;
}

// ---- the specialists whose sign-in is blocked ----

export interface BlockedSearch {
  page?: number;
}

export function parseBlockedSearch(raw: Readonly<Record<string, unknown>>): BlockedSearch {
  const page = pageOf(raw["page"]);
  return page === undefined ? {} : { page };
}

/** What the list asks hg-auth for: the accounts holding the nurse's role whose sign-in is blocked. */
export const BLOCKED_SPECIALISTS: AccountsFilter = {
  role: "nurse",
  status: "blocked",
  sort: "full_name",
};

/** Any list's view at another page. */
export function withPage<Search extends { page?: number }>(search: Search, page: number): Search {
  const { page: _, ...rest } = search;
  return (page > 1 ? { ...rest, page } : rest) as Search;
}
